import type { ProductItemAttributesValue } from '@athena-lib/ProductWizard/product/model/ProductItemAttributesInterface';
import type { ProductAttrComputer } from '@athena-lib/ProductWizard/product/helper/ProductAttrComputer';
import type { ProductProcessorInterface } from '../../product/processor/ProductProcessorInterface';
import type { ProductItem } from '../../product/model/ProductItem';
import type { ProductCherryPickHelper } from '../../model/ProductCherryPickHelper';

type CherryPickValue = string | number | boolean;

type ProductItemAndCurrentValue = {
  productItem: ProductItem;
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  currentValue: any;
};

export class AutoPickAttrProcessor implements ProductProcessorInterface {
  constructor(
    public autoOptDefaultValue: string[],
    protected cherryPicks: ProductCherryPickHelper,
    protected ignoreAttributeFromAutoPickerCallback: (attributeName: string) => boolean,
    protected isAttributeRequiredCallback: (attributeName: string) => boolean,
  ) {}

  public invoke(productItem: ProductItem, attrComputer: ProductAttrComputer): boolean {
    let autoPicks: { runAutoPick: boolean; iteration: number } = {
      runAutoPick: true,
      iteration: 0,
    };
    autoPicks = this.startRunAutoPick('attributes', attrComputer, productItem);
    autoPicks.runAutoPick = true;
    // Imperial_units is set to null after startRunAutoPick with attributes for some reason...
    productItem.attributes['imperial_units'] = productItem.attributes['imperial_units'] ?? false; // Fallback to false
    autoPicks = this.startRunAutoPick('cherries', attrComputer, productItem);

    return !autoPicks.runAutoPick && autoPicks.iteration == 1;
  }

  private startRunAutoPick(
    type: 'cherries' | 'attributes',
    attrComputer: ProductAttrComputer,
    productItem: ProductItem,
  ): { runAutoPick: boolean; iteration: number } {
    let runAutoPick: boolean = true;
    let iteration: number = 0;
    switch (type) {
      case 'cherries':
        while (runAutoPick) {
          iteration++;
          attrComputer.evaluate(productItem);
          runAutoPick = this.tryAutoPickFromCherries(productItem, attrComputer);

          if (iteration >= 5) {
            // TODO: Throw error to detect if this ever happens
            // Protected from endless loops
            break;
          }
        }
        return { runAutoPick, iteration };
      case 'attributes':
        while (runAutoPick) {
          iteration++;
          attrComputer.evaluate(productItem);
          runAutoPick = this.autoPickAttributes(productItem, attrComputer);

          if (iteration >= 5) {
            // TODO: Throw error to detect if this ever happens
            // Protected from endless loops
            break;
          }
        }
        return { runAutoPick, iteration };
    }
  }

  protected autoPickAttributes(
    productItem: ProductItem,
    attrComputer: ProductAttrComputer,
  ): boolean {
    const valuesAsString = productItem.attributesToString();

    for (const attributeName in attrComputer.attributes) {
      if (this.ignoreAttributeFromAutoPickerCallback(attributeName)) {
        continue;
      }

      let currentValue = productItem?.getAttrValue(attributeName);
      if (currentValue || this.isAttributeRequiredCallback(attributeName)) {
        const cherryPick = this.cherryPicks.getAttrValue(attributeName);
        currentValue = attrComputer.isNumberType(attributeName)
          ? Number(currentValue)
          : currentValue;
        this.autoPick(
          { productItem: productItem, currentValue: currentValue },
          attrComputer,
          attributeName,
          cherryPick,
        );
      }
    }

    return valuesAsString != productItem.attributesToString();
  }

  protected tryAutoPickFromCherries(
    productItem: ProductItem,
    attrComputer: ProductAttrComputer,
  ): boolean {
    const valuesAsString = productItem.attributesToString();

    for (const attributeName in attrComputer.attributes) {
      if (this.cherryPicks.hasAttr(attributeName)) {
        const cherryPick = this.cherryPicks.getAttrValue(attributeName);
        let currentValue = productItem?.getAttrValue(attributeName);
        currentValue = attrComputer.isNumberType(attributeName)
          ? Number(currentValue)
          : currentValue;
        if (!currentValue || currentValue != cherryPick) {
          this.autoPick(
            { productItem: productItem, currentValue: currentValue },
            attrComputer,
            attributeName,
            cherryPick,
          );
        }
      }
    }

    return valuesAsString != productItem.attributesToString();
  }

  protected autoPick(
    item: ProductItemAndCurrentValue,
    attrComputer: ProductAttrComputer,
    attributeName: string,
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    cherryPick: any,
  ) {
    if (attrComputer?.isMultiValue(attributeName)) {
      if (!Array.isArray(item.currentValue)) {
        item.currentValue = [item.currentValue];
      }

      let newValues = (item.currentValue as Array<CherryPickValue>).filter((value) =>
        attrComputer.isAvailable(attributeName, value),
      );
      if (cherryPick) {
        cherryPick = (cherryPick as Array<CherryPickValue>).filter(
          (value) => attrComputer.isAvailable(attributeName, value) && newValues.indexOf(value) < 0,
        );
        newValues = [...newValues, ...(cherryPick as Array<CherryPickValue>)];
      }
      this.cherryPicks.maybeRegisterALost(
        attributeName,
        newValues as string | number | boolean | string[] | number[] | null,
      );
      item.productItem?.setAttrValue(attributeName, newValues as ProductItemAttributesValue);
    } else {
      this.autPickNoneMulti(item, attrComputer, attributeName, cherryPick);
    }
  }

  private autPickNoneMulti(
    item: ProductItemAndCurrentValue,
    attrComputer: ProductAttrComputer,
    attributeName: string,
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    cherryPick: any,
  ) {
    const defaultValue = attrComputer.getDefaultValue(attributeName);
    let newValue =
      (item.currentValue == null && !this.isAttributeRequiredCallback(attributeName)) ||
      attrComputer.isAvailable(attributeName, item.currentValue)
        ? item.currentValue
        : defaultValue;
    if (!newValue) {
      newValue = this.getNewValue(attrComputer, attributeName);
    }
    if (cherryPick) {
      newValue =
        this.getNewValueWithCherryPick(cherryPick, attrComputer, attributeName) ?? newValue;
    }
    this.cherryPicks.maybeRegisterALost(attributeName, newValue);
    item.productItem.setAttrValue(attributeName, newValue);
  }

  private getNewValue(
    attrComputer: ProductAttrComputer,
    attributeName: string,
  ): string | number | boolean | unknown[] | null {
    const defaultValue = attrComputer.getDefaultValue(attributeName);
    const value = attrComputer.isDynamicValue(attributeName) && defaultValue ? defaultValue : null;
    return value;
  }

  private getNewValueWithCherryPick(
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    cherryPick: any,
    attrComputer: ProductAttrComputer,
    attributeName: string,
  ): string | number | boolean | string[] | number[] | null {
    const value =
      attrComputer.isAvailable(attributeName, cherryPick as CherryPickValue) &&
      !attrComputer.isDynamicValue(attributeName)
        ? cherryPick
        : null;
    return value;
  }

  /**
   * TODO: Not sure what difference this does. Turned it off to see if we really need it.
   * Whether an attribute should try to autopick default value if no value has been cherry picked.
   *
   * @param attributeName
   */
  public shouldAutoOptForDefaultValue(attributeName: string): boolean {
    return this.autoOptDefaultValue.indexOf(attributeName) >= 0;
  }
}
